import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Divider,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { ODOSModule } from '../../services/odosService';

interface User {
  _id: string;
  name: string;
  email: string;
  role?: string;
}

interface TeamMember {
  user: string;
  role: string;
  responsibilities: string[];
}

interface TeamEditDialogProps {
  open: boolean;
  onClose: () => void;
  module: ODOSModule | null;
  onSave: (teamData: any) => Promise<void>;
  users: User[];
}

const TeamEditDialog: React.FC<TeamEditDialogProps> = ({
  open,
  onClose,
  module,
  onSave,
  users
}) => {
  const [teamLead, setTeamLead] = useState<User | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (module && open) {
      // Set team lead
      if (module.team?.lead) {
        const leadId = typeof module.team.lead === 'object' && module.team.lead !== null && '_id' in module.team.lead
          ? (module.team.lead as any)._id
          : module.team.lead;
        const leadUser = users.find(u => u._id === leadId);
        setTeamLead(leadUser || null);
      } else {
        setTeamLead(null);
      }

      // Set team members
      if (module.team?.members && Array.isArray(module.team.members)) {
        setMembers(module.team.members.map((m: any) => ({
          user: typeof m.user === 'object' && m.user !== null && '_id' in m.user ? m.user._id : m.user,
          role: m.role || '',
          responsibilities: m.responsibilities || []
        })));
      } else {
        setMembers([]);
      }
    }
  }, [module, open, users]);

  const handleAddMember = () => {
    setMembers([...members, { user: '', role: '', responsibilities: [] }]);
  };

  const handleRemoveMember = (index: number) => {
    setMembers(members.filter((_, i) => i !== index));
  };

  const handleMemberChange = (index: number, field: string, value: any) => {
    const updated = [...members];
    updated[index] = { ...updated[index], [field]: value };
    setMembers(updated);
  };

  const handleAddResponsibility = (index: number, responsibility: string) => {
    if (!responsibility.trim()) return;
    const updated = [...members];
    updated[index].responsibilities = [...updated[index].responsibilities, responsibility.trim()];
    setMembers(updated);
  };

  const handleRemoveResponsibility = (memberIndex: number, respIndex: number) => {
    const updated = [...members];
    updated[memberIndex].responsibilities = updated[memberIndex].responsibilities.filter((_, i) => i !== respIndex);
    setMembers(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate
      const invalidMembers = members.filter(m => !m.user || !m.role);
      if (invalidMembers.length > 0) {
        setError('All team members must have a user and role assigned');
        setSaving(false);
        return;
      }

      const teamData = {
        lead: teamLead?._id || null,
        members: members.map(m => ({
          user: m.user,
          role: m.role,
          responsibilities: m.responsibilities
        })),
        stakeholders: module?.team?.stakeholders || []
      };

      await onSave(teamData);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save team assignments');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="team-edit-dialog-title"
    >
      <DialogTitle id="team-edit-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ color: '#003366', fontFamily: 'Merriweather, serif' }}>
            Edit Team - {module?.name}
          </Typography>
          <IconButton onClick={handleClose} disabled={saving} aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Team Lead */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#003366' }}>
            Team Lead
          </Typography>
          <Autocomplete
            value={teamLead}
            onChange={(_, newValue) => setTeamLead(newValue)}
            options={users}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select team lead"
                variant="outlined"
              />
            )}
            disabled={saving}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Team Members */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#003366' }}>
              Team Members ({members.length})
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddMember}
              variant="outlined"
              size="small"
              disabled={saving}
              sx={{ borderColor: '#003366', color: '#003366' }}
            >
              Add Member
            </Button>
          </Box>

          {members.length === 0 ? (
            <Alert severity="info">No team members assigned. Click "Add Member" to add team members.</Alert>
          ) : (
            <Stack spacing={2}>
              {members.map((member, index) => {
                const selectedUser = users.find(u => u._id === member.user);
                return (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      bgcolor: '#f9f9f9'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Autocomplete
                          value={selectedUser || null}
                          onChange={(_, newValue) => handleMemberChange(index, 'user', newValue?._id || '')}
                          options={users}
                          getOptionLabel={(option) => `${option.name} (${option.email})`}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="User"
                              placeholder="Select user"
                              size="small"
                              required
                            />
                          )}
                          disabled={saving}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          label="Role"
                          value={member.role}
                          onChange={(e) => handleMemberChange(index, 'role', e.target.value)}
                          placeholder="e.g., Developer, QA, Designer"
                          size="small"
                          fullWidth
                          required
                          disabled={saving}
                        />
                      </Box>
                      <IconButton
                        onClick={() => handleRemoveMember(index)}
                        color="error"
                        size="small"
                        disabled={saving}
                        aria-label="Remove team member"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    {/* Responsibilities */}
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                        Responsibilities:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        {member.responsibilities.map((resp, respIndex) => (
                          <Chip
                            key={respIndex}
                            label={resp}
                            size="small"
                            onDelete={() => handleRemoveResponsibility(index, respIndex)}
                            disabled={saving}
                          />
                        ))}
                      </Box>
                      <TextField
                        placeholder="Add responsibility and press Enter"
                        size="small"
                        fullWidth
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const input = e.target as HTMLInputElement;
                            handleAddResponsibility(index, input.value);
                            input.value = '';
                          }
                        }}
                        disabled={saving}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{
            bgcolor: '#003366',
            '&:hover': { bgcolor: '#002244' }
          }}
        >
          {saving ? 'Saving...' : 'Save Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamEditDialog;
